import React, { useEffect, useState } from "react";
import Swal from 'sweetalert2';
import {
    Button,
    FormGroup,
    Label,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Nav,
    NavItem,
    NavLink,
    Card,
    CardBody,
    CardTitle,
    CardText,
} from "reactstrap";
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import Loader from "../../../../../components/common/Loader";
import Select from 'react-select';
import {
    showLoadingAlert,
    showSuccessAlert,
    showErrorAlert
} from "../../../../../components/custom/sweetalertHelper";
import { createExportGoogle } from "../../../../../slices/veloxity/integration/export/createExportGoogle";
import { getExportPlans } from "../../../../../slices/veloxity/integration/export/getExportPlans";


const GoogleAdModal = ({ modalTitle, isModalOpen, toggleModal, selectedButton }) => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('1');
    const [description, setDescription] = useState("");
    const [membershipLifeSpan, setMembershipLifeSpan] = useState(""); 

    const veloxityAudience = useSelector((state) => state.findAllAudienceReducer?.payload || []);
    const targetAudience = useSelector((state) => state.targetAllAudience?.payload || []);
    const adAccounts = useSelector((state) => state.fetchConfigs?.data || []); 

    const loading = useSelector(
        (state) =>
            state.findAllAudienceReducer?.loading ||
            state.targetAllAudience?.loading ||
            state.fetchConfigs?.loading
    );

    const [selectedAudience, setSelectedAudience] = useState(null);
    const [selectedAdAccount, setSelectedAdAccount] = useState(null);

    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
            setSelectedAudience(null);
        }
    };

    const handleSubmit = () => {
        if (selectedAudience && description && selectedAdAccount) {
            showLoadingAlert('Submitting...', 'Please wait while your request is being processed.');

            const selectedMetaData = selectedAdAccount.metaData; 

            const submitData = {
                audienceId: selectedAudience.value,
                integrationConfigId: selectedAdAccount.id, 
                description: description,
                clientCustomerId: selectedMetaData.mccCustomerId, 
                membershipLifeSpan: membershipLifeSpan || 30 
            };

            dispatch(createExportGoogle(submitData))
                .then(() => {
                    Swal.close();
                    showSuccessAlert('Success', 'Google export plan has been created successfully.');
                    dispatch(getExportPlans());
                    toggleModal();
                })
                .catch((error) => {
                    Swal.close();
                    showErrorAlert('Error', 'There was an error submitting the Google export plan.');
                });
        }
    };

    const filterAndSortAudiences = (audiences) => {
        return audiences
            .filter((audience) => audience.status === "SUCCEEDED")
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    const prepareAudienceOptions = (audiences) => {
        const filteredAndSortedAudiences = filterAndSortAudiences(audiences);
        return filteredAndSortedAudiences.map((audience) => ({
            value: audience.audienceId,
            label: audience.name,
            size: audience.audienceSize,
            audience,
        }));
    };


    const audienceOptions = prepareAudienceOptions(
        activeTab === '1' ? veloxityAudience : targetAudience
    );

    const adAccountOptions = adAccounts.map((account) => ({
        value: account.id,
        label: account.name,
        id: account.id, 
        metaData: account.metaData, 
    }));

    return (
        <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
            <ModalHeader toggle={toggleModal}>{modalTitle}</ModalHeader>
            <ModalBody>
                {loading ? (
                                     <div className="text-center">
                                     <Loader />
                                   </div>
                ) : (
                    <>

                        <Nav tabs>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === '1' })}
                                    onClick={() => { toggleTab('1'); }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Audience
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: activeTab === '2' })}
                                    onClick={() => { toggleTab('2'); }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Target Audience
                                </NavLink>
                            </NavItem>
                        </Nav>

                        <div className="mt-3">
                            <FormGroup>
                                <Label style={{ fontWeight: 'bold' }}>
                                    Select {activeTab === '1' ? 'Audience' : 'Target Audience'}
                                </Label>
                                <Select
                                    options={audienceOptions}
                                    value={selectedAudience}
                                    onChange={(option) => setSelectedAudience(option)}
                                    isSearchable
                                    placeholder={`Search and select ${activeTab === '1' ? 'an audience' : 'a target audience'}...`}
                                    styles={{
                                        control: (provided) => ({
                                          ...provided,
                                          width: '103%',
                                          marginLeft: '-1.5%',
                                          borderRadius: '0.25rem',
                                          borderColor: '#ced4da',
                                          minHeight: 'calc(1.5em + 0.75rem + 2px)'
                                        }),
                                      }}
                                />
                            </FormGroup>
                        </div>

                        {selectedAudience && (
                            <Card className="mt-3 mx-auto" style={{ width: '98%' }}>
                                <CardBody>
                                    <CardTitle tag="h5" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                                        Audience Details
                                    </CardTitle>
                                    <CardText>
                                        <strong>Name:</strong> {selectedAudience.audience.name}
                                    </CardText>
                                    <CardText>
                                        <strong>Audience Size:</strong> {selectedAudience.audience.audienceSize}
                                    </CardText>
                                    <CardText>
                                        <strong>Created Date:</strong> {new Date(selectedAudience.audience.updateDate).toLocaleDateString()}
                                    </CardText>
                                </CardBody>
                            </Card>
                        )}

                        <FormGroup className="mt-3">
                            <Label style={{ fontWeight: 'bold' }}>Select Ad Account</Label>
                            <Select
                                options={adAccountOptions}
                                value={selectedAdAccount}
                                onChange={(option) => setSelectedAdAccount(option)}
                                isSearchable
                                placeholder="Search and select an ad account..."
                                styles={{
                                    control: (provided) => ({
                                      ...provided,
                                      width: '103%',
                                      marginLeft: '-1.5%',
                                      borderRadius: '0.25rem',
                                      borderColor: '#ced4da',
                                      minHeight: 'calc(1.5em + 0.75rem + 2px)'
                                    }),
                                  }}
                            />
                        </FormGroup>

                        <FormGroup className="mt-3">
                            <Label style={{ fontWeight: 'bold' }}>Description</Label>
                            <textarea
                                className="form-control"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Enter a description"
                            />
                        </FormGroup>

                        <FormGroup className="mt-3">
                            <Label style={{ fontWeight: 'bold' }}>Membership LifeSpan (Days)</Label>
                            <input
                                type="number"
                                className="form-control"
                                value={membershipLifeSpan}
                                onChange={(e) => setMembershipLifeSpan(e.target.value)}
                                placeholder="Enter membership lifespan"
                            />
                        </FormGroup>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggleModal}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={!selectedAudience || !selectedAdAccount || !description}
                >
                    Submit
                </Button>

            </ModalFooter>
        </Modal>
    );
};

export default GoogleAdModal;
